import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { I18nextProvider } from 'react-i18next';
import { TooltipProvider } from '~/design-system';
import { initRevealChoreography } from '~/design-system/motion/reveal';
import { createAppRouter } from './app/router';
import { ApiContext } from './platform/data/queries';
import { createMockApi } from './platform/data/api';
import { createHttpApi } from './platform/data/http';
import { createLegacyApi } from './platform/data/legacy';
import { setApi } from './platform/data/apiSingleton';
import i18next, { initI18n } from './platform/i18n/i18n';
import { applyContextualDefaults } from './platform/appearance/appearance';
import './design-system/styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: 0 },
  },
});

function resolveDataSource(): 'mock' | 'legacy' | 'api' {
  const fromQuery = new URLSearchParams(window.location.search).get('data');
  const fromEnv = import.meta.env['VITE_DATA_SOURCE'] as string | undefined;
  const raw = (fromQuery ?? fromEnv ?? 'legacy').toLowerCase();
  if (raw === 'mock' || raw === 'api' || raw === 'legacy') return raw;
  return 'legacy';
}

async function boot() {
  applyContextualDefaults();

  const source = resolveDataSource();
  const mock = createMockApi();
  const api =
    source === 'mock'
      ? mock
      : source === 'api'
        ? createHttpApi({
            baseUrl: (import.meta.env['VITE_API_URL'] as string) || 'http://localhost:3000',
            fallback: mock,
          })
        : createLegacyApi(mock);
  setApi(api);

  const [session] = await Promise.all([api.getSession(), initI18n()]);

  document.documentElement.lang = i18next.resolvedLanguage ?? 'en';

  const router = createAppRouter(session);
  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');

  createRoot(container).render(
    <StrictMode>
      <I18nextProvider i18n={i18next}>
        <QueryClientProvider client={queryClient}>
          <ApiContext.Provider value={api}>
            <TooltipProvider>
              <RouterProvider router={router} />
            </TooltipProvider>
          </ApiContext.Provider>
        </QueryClientProvider>
      </I18nextProvider>
    </StrictMode>,
  );

  initRevealChoreography();
}

void boot();
